import { useState } from 'react';
import { Search, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/utils/cn';
import {
  panelFilterCount,
  type ReferralListFilters,
} from '../../../hooks/useReferralList';
import type { ReferralListDirection } from '../../../types/engagement-metrics.types';
import { DIRECTION_LABELS } from './referralsCopy';

const selectClass =
  'h-9 rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#15543e]/20';

const DateRange = ({
  label,
  from,
  to,
  onChange,
}: {
  label: string;
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-medium text-gray-500">{label}</span>
    <div className="flex flex-wrap items-center gap-1">
      <input type="date" aria-label={`${label} from`} className={selectClass} value={from} onChange={(e) => onChange(e.target.value, to)} />
      <span className="text-gray-400">–</span>
      <input type="date" aria-label={`${label} to`} className={selectClass} value={to} onChange={(e) => onChange(from, e.target.value)} />
    </div>
  </div>
);

type ReferralsFiltersProps = {
  filters: ReferralListFilters;
  onChange: (patch: Partial<ReferralListFilters>) => void;
  onClear: () => void;
};

/** Variant A toolbar (ticket 07): search, Billable segmented, collapsible "Filters (n)" panel. */
export const ReferralsFilters = ({ filters, onChange, onClear }: ReferralsFiltersProps) => {
  const [open, setOpen] = useState(false);
  const count = panelFilterCount(filters);

  const toggleDirection = (direction: ReferralListDirection, checked: boolean) =>
    onChange({
      directions: checked
        ? [...filters.directions, direction]
        : filters.directions.filter((d) => d !== direction),
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[260px] max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={filters.search}
            onChange={(e) => onChange({ search: e.target.value })}
            placeholder="Search any name: referrer, referred, care seeker…"
            aria-label="Search referrals"
            className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-[#15543e] focus:outline-none focus:ring-2 focus:ring-[#15543e]/20"
          />
        </div>
        <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" role="group" aria-label="Billable">
          {(['ALL', 'BILLABLE', 'NON_BILLABLE'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={filters.billable === value}
              onClick={() => onChange({ billable: value })}
              className={cn(
                'rounded px-3 py-1 text-sm',
                filters.billable === value ? 'bg-[#15543e] text-white' : 'text-gray-600 hover:bg-gray-50',
              )}
            >
              {value === 'ALL' ? 'All' : value === 'BILLABLE' ? 'Billable' : 'Non-billable'}
            </button>
          ))}
        </div>
        <Button variant="outline" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          Filters{count > 0 && ` (${count})`}
        </Button>
        {count > 0 && (
          <button type="button" className="text-sm text-[#15543e] hover:underline" onClick={onClear}>
            Clear all
          </button>
        )}
      </div>

      {open && (
        <div className="grid gap-5 rounded-xl border border-gray-200 bg-white p-4 md:grid-cols-[auto_auto_1fr]">
          <fieldset>
            <legend className="mb-2 text-xs font-medium text-gray-500">Direction</legend>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
              {(Object.keys(DIRECTION_LABELS) as ReferralListDirection[]).map((direction) => (
                <label key={direction} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="accent-[#15543e]"
                    checked={filters.directions.includes(direction)}
                    onChange={(e) => toggleDirection(direction, e.target.checked)}
                  />
                  {DIRECTION_LABELS[direction]}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="flex flex-col gap-3">
            <select
              aria-label="Origin"
              className={selectClass}
              value={filters.origin}
              onChange={(e) => onChange({ origin: e.target.value as ReferralListFilters['origin'] })}
            >
              <option value="">All origins</option>
              <option value="PLATFORM">Platform</option>
              <option value="MANUAL">Manual</option>
            </select>
            <select
              aria-label="Care seeker"
              className={selectClass}
              value={filters.careSeeker}
              onChange={(e) => onChange({ careSeeker: e.target.value as ReferralListFilters['careSeeker'] })}
            >
              <option value="">Any care seeker</option>
              <option value="WITH">With care seeker</option>
              <option value="WITHOUT">Without care seeker</option>
            </select>
          </div>
          <div className="flex flex-col gap-3">
            <DateRange
              label="Referral date"
              from={filters.referralDateFrom}
              to={filters.referralDateTo}
              onChange={(referralDateFrom, referralDateTo) => onChange({ referralDateFrom, referralDateTo })}
            />
            <DateRange
              label="Created date"
              from={filters.createdDateFrom}
              to={filters.createdDateTo}
              onChange={(createdDateFrom, createdDateTo) => onChange({ createdDateFrom, createdDateTo })}
            />
          </div>
        </div>
      )}
    </div>
  );
};
